import React, {useState, useEffect} from "react";
import DaumPostcodeEmbed from 'react-daum-postcode';
import { useNavigate } from 'react-router-dom';
import useUserStore from "../../../store/userStore";
import { formatPhoneNumber, validateBusinessNumber, formatBusinessNumber } from "../../../utils/validation";
import { useLanguage } from "../../../hooks/useLanguage";

// 회원 유형 상수 정의
const MEMBER_TYPES = {
  GENERAL: "1",
  STUDENT: "2",
  DIRECTOR: "3",
  PRODUCER: "4"
};

function JoinForm(){
  const { t } = useLanguage();
  const { joinVerificationInfo } = useUserStore();
  const [memberType, setMemberType] = useState(MEMBER_TYPES.GENERAL);
  
  // 앱 환경 감지 함수
  const isInApp = () => {
    const userAgent = navigator.userAgent || '';
    return (
      /DayPay|daypay|AppWebView|wv|inapp|app/i.test(userAgent) ||
      typeof window.ReactNativeWebView !== 'undefined' ||
      typeof window.AppInterface !== 'undefined' ||
      window.location.href.includes('app=true')
    );
  };

  // 개인 주소 관련 상태
  const [personalAddress, setPersonalAddress] = useState({
    zonecode: '',
    roadAddress: '',
    jibunAddress: ''
  });

  // 사업자 주소 관련 상태
  const [businessAddress, setBusinessAddress] = useState({
    zonecode: '',
    roadAddress: '',
    jibunAddress: ''
  });

  // 우편번호 모달 상태 분리
  const [isPersonalPostcodeOpen, setIsPersonalPostcodeOpen] = useState(false);
  const [isBusinessPostcodeOpen, setIsBusinessPostcodeOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    member_phone2: '',
    verificationCode: '',
    email: '',
    business_name: '',
    business_num: '',
    business_ceo: '',
    business_category: '',
    business_item: '',
    companyInfo: {
        name: '',
        businessFile: null,
        businessNumber: '',
        businessType: '',
        address: {
            zipcode: '',
            address1: '',
            address2: ''
        }
    }
  });

  // 파일 상태 추가
  const [files, setFiles] = useState({
    adultFile: null,
    studentFile: null,
    producerFile: null
  });

  // 개인 상세주소 상태 추가
  const [personalDetailAddress, setPersonalDetailAddress] = useState('');

  // 사업자 등록 번호 유효성 검증 상태 추가
  const [businessNumError, setBusinessNumError] = useState('');

  // 성별, 생년월일, 주민등록번호 상태 추가
  // 주민등록번호 상태 수정 (값 전송용이 아닌 UI용)
  const [personalInfo, setPersonalInfo] = useState({
    gender: '',
    birthDate: '',
    residentNumber1: '',
    residentNumber2: '' // UI 표시용
  });

  const navigate = useNavigate();

  // 컴포넌트가 마운트될 때 본인인증 정보로 폼 데이터 초기화
  useEffect(() => {
    
    setFormData(prevData => ({
      ...prevData,
      name: joinVerificationInfo.username || '', // 본인인증에서 받은 이름
      phone: formatPhoneNumber(joinVerificationInfo.mem_phone) || '', // 본인인증에서 받은 휴대폰 번호
      memberKind: joinVerificationInfo.memberKind || '', // 본인인증에서 받은 회원 유형
      birthDate: joinVerificationInfo.birthdate || '', // 본인인증에서 받은 생년월일
      gender: joinVerificationInfo.gender || '', // 본인인증에서 받은 성별
    }));

    // 생년월일과 주민등록번호 파싱
    if (joinVerificationInfo.birthdate) {
      const birthdate = joinVerificationInfo.birthdate; // YYYYMMDD 형식
      
      // 주민등록번호 앞자리 (YYMMDD 형식)
      const residentFront = birthdate.substring(2, 8); // YYMMDD
      
      setPersonalInfo(prev => ({
        ...prev,
        birthDate: birthdate,
        residentNumber1: residentFront
      }));
    }

    // 성별 설정 (0: 여자, 1: 남자)
    if (joinVerificationInfo.gender !== undefined && joinVerificationInfo.gender !== '') {
      const genderValue = joinVerificationInfo.gender === '0' ? 'female' : 'male';
      
      setPersonalInfo(prev => ({
        ...prev,
        gender: genderValue
      }));
    }

    // 주민등록번호 뒷자리 첫 번째 숫자 계산
    if (joinVerificationInfo.birthdate && joinVerificationInfo.gender !== undefined) {
      const birthYear = parseInt(joinVerificationInfo.birthdate.substring(0, 4));
      const gender = joinVerificationInfo.gender; // 0: 여자, 1: 남자
      
      let genderDigit = '';
      
      if (birthYear >= 2000) {
        // 2000년 이후 출생
        genderDigit = gender === '0' ? '4' : '3'; // 여자: 4, 남자: 3
      } else {
        // 1900년대 출생
        genderDigit = gender === '0' ? '2' : '1'; // 여자: 2, 남자: 1
      }
      
      setPersonalInfo(prev => ({
        ...prev,
        residentNumber2: genderDigit
      }));
    }

  }, [joinVerificationInfo]);

  // 개인 주소 핸들러
  const handlePersonalPostcodeOpen = (e) => {
    e.preventDefault();
    setIsPersonalPostcodeOpen(true);
  };

  const handlePersonalPostcodeComplete = (data) => {
    const newAddress = {
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      jibunAddress: data.jibunAddress
    };
    setPersonalAddress(newAddress);
    setIsPersonalPostcodeOpen(false);
  };

  const handlePersonalPostcodeClose = () => {
    setIsPersonalPostcodeOpen(false);
  };

  // 사업자 주소 핸들러
  const handleBusinessPostcodeOpen = (e) => {
    e.preventDefault();
    setIsBusinessPostcodeOpen(true);
  };

  const handleBusinessPostcodeComplete = (data) => {
    const newAddress = {
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      jibunAddress: data.jibunAddress
    };
    setBusinessAddress(newAddress);
    setFormData(prevData => ({
      ...prevData,
      companyInfo: {
        ...prevData.companyInfo,
        address: {
          zipcode: data.zonecode,
          address1: data.roadAddress,
          address2: ''
        }
      }
    }));
    setIsBusinessPostcodeOpen(false);
  };

  const handleBusinessPostcodeClose = () => {
    setIsBusinessPostcodeOpen(false);
  };

  const handleMemberTypeChange = (e) => {
    setMemberType(e.target.value);
  };

  // 파일 변경 핸들러 추가
  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  // 폼 데이터 업데이트 핸들러
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    if (id === 'member_phone2') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [id]: formatPhoneNumber(numericValue)
      }));
    } else if (id === 'business_num') {
      // 사업자 등록 번호 처리
      const numericValue = value.replace(/[^0-9]/g, '');
      
      // 10자리 초과 입력 방지
      if (numericValue.length > 10) return;
      
      const formattedValue = formatBusinessNumber(numericValue);
      
      setFormData(prev => ({
        ...prev,
        [id]: formattedValue
      }));
      
      // 실시간 유효성 검증
      if (numericValue.length === 10) {
        const validation = validateBusinessNumber(numericValue);
        setBusinessNumError(validation.message);
      } else if (numericValue.length > 0) {
        setBusinessNumError('사업자 등록 번호는 10자리 숫자입니다.');
      } else {
        setBusinessNumError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [id]: value
      }));
    }
  };

  // 사업자 정보 입력 핸들러
  const handleCompanyInfoChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      companyInfo: {
        ...prev.companyInfo,
        [id]: value
      }
    }));
  };

  // 개인정보 입력 핸들러 추가
  const handlePersonalInfoChange = (e) => {
    const { id, value } = e.target;
    
    if (id === 'birthDate') {
      // 생년월일: 숫자만 입력, 8자리 제한
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 8) {
        setPersonalInfo(prev => ({
          ...prev,
          [id]: numericValue
        }));
      }
    } else if (id === 'residentNumber1') {
      // 주민등록번호 앞자리: 숫자만 입력, 6자리 제한
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 6) {
        setPersonalInfo(prev => ({
          ...prev,
          [id]: numericValue
        }));
      }
    } else if (id === 'residentNumber2') {
      // 주민등록번호 뒷자리: 숫자만 입력, 6자리 제한 (UI용)
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 6) {
        setPersonalInfo(prev => ({
          ...prev,
          [id]: numericValue
        }));
      }
    } else {
      setPersonalInfo(prev => ({
        ...prev,
        [id]: value
      }));
    }
  };

  // 폼 유효성 검사
  const isFormValid = () => {
    // 사업자 회원가입인 경우
    if (joinVerificationInfo.memberKind === "corporate") {
      const corporateFieldsValid = 
        formData.name &&
        formData.phone &&
        formData.email &&
        formData.business_name &&
        formData.business_num &&
        formData.member_phone2 &&
        !businessNumError && // 사업자 등록 번호 오류가 없어야 함
        formData.business_ceo &&
        formData.business_category &&
        formData.business_item &&
        personalAddress.zonecode &&
        personalAddress.roadAddress &&
        personalDetailAddress &&
        files.producerFile && // 사업자등록증 필수
        personalInfo.gender &&
        personalInfo.birthDate;
      // residentNumber1, residentNumber2는 UI용이므로 검증에서 제외
      
      return corporateFieldsValid;
    }

    // 개인 회원가입인 경우 (기존 로직)
    const commonFieldsValid = 
      files.adultFile &&
      formData.name &&
      formData.phone &&
      formData.email &&
      formData.member_phone2 &&
      personalAddress.zonecode &&
      personalAddress.roadAddress &&
      personalDetailAddress &&
      personalInfo.gender &&
      personalInfo.birthDate;
    // residentNumber1, residentNumber2는 UI용이므로 검증에서 제외

    switch(memberType) {
      case MEMBER_TYPES.STUDENT:
        return commonFieldsValid && files.studentFile;

      case MEMBER_TYPES.PRODUCER:
        return commonFieldsValid && 
          files.producerFile && 
          formData.companyInfo.name && 
          formData.companyInfo.businessNumber && 
          formData.companyInfo.businessType &&
          businessAddress.zonecode &&
          businessAddress.roadAddress &&
          formData.companyInfo.address.address2;

      case MEMBER_TYPES.GENERAL:
      case MEMBER_TYPES.DIRECTOR:
        return commonFieldsValid;

      default:
        return commonFieldsValid;
    }
  };

  // 개인 상세주소 입력 핸들러
  const handlePersonalDetailAddressChange = (e) => {
    setPersonalDetailAddress(e.target.value);
  };

  // 사업자 상세주소 입력 핸들러
  const handleBusinessDetailAddressChange = (e) => {
    setFormData(prev => ({
      ...prev,
      companyInfo: {
        ...prev.companyInfo,
        address: {
          ...prev.companyInfo.address,
          address2: e.target.value
        }
      }
    }));
  };

  // 다음 버튼 클릭 핸들러 수정
  const handleNextClick = () => {
    if (isFormValid()) {
      const joinFormData = {
        mailing: 'Y',
        sms: 'Y',
        push: 'Y',
        push_id: '',
        ci: joinVerificationInfo.ci,
        di: joinVerificationInfo.di,
        username: formData.name,
        mem_phone: formData.phone,
        mem_phone2: formData.member_phone2,
        mem_type: memberType,
        email: formData.email,
        mem_zipcode: personalAddress.zonecode,
        mem_address_street: personalAddress.roadAddress,
        mem_address_detail: personalDetailAddress,
        business_name: formData.business_name,
        business_num: formData.business_num,
        business_ceo: formData.business_ceo,
        business_category: formData.business_category,
        business_item: formData.business_item,
        member_kind: joinVerificationInfo.memberKind,
        // 개인정보 추가 (주민등록번호 제외)
        mem_gender: joinVerificationInfo.gender,
        mem_birthday: joinVerificationInfo.birthdate
      };

      // 제작사인 경우 사업자 정보 추가
      if (memberType === MEMBER_TYPES.PRODUCER) {
        joinFormData.business_info = {
          business_name: formData.companyInfo.name,
          business_num: formData.companyInfo.businessNumber,
          business_ceo: formData.companyInfo.business_ceo,
          business_kind: formData.companyInfo.businessType,
          business_zipcode: businessAddress.zonecode,
          business_address_street: businessAddress.roadAddress,
          business_address_detail: formData.companyInfo.address.address2
        };
      }

      // 앱 환경 체크
      if (isInApp()) {
        // 앱에서는 localStorage 사용
        try {
          localStorage.setItem('joinFormData', JSON.stringify(joinFormData));
          
          // 파일 정보를 Base64로 변환하여 저장
          const filePromises = [];
          
          if (files.adultFile) {
            filePromises.push(
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                  key: 'identification_img',
                  data: e.target.result,
                  name: files.adultFile.name,
                  type: files.adultFile.type,
                  size: files.adultFile.size
                });
                reader.readAsDataURL(files.adultFile);
              })
            );
          }
          
          if (files.studentFile) {
            filePromises.push(
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                  key: 'student_id_img',
                  data: e.target.result,
                  name: files.studentFile.name,
                  type: files.studentFile.type,
                  size: files.studentFile.size
                });
                reader.readAsDataURL(files.studentFile);
              })
            );
          }
          
          if (files.producerFile) {
            filePromises.push(
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                  key: 'business_registration_img',
                  data: e.target.result,
                  name: files.producerFile.name,
                  type: files.producerFile.type,
                  size: files.producerFile.size
                });
                reader.readAsDataURL(files.producerFile);
              })
            );
          }

          Promise.all(filePromises).then((fileData) => {
            try {
              localStorage.setItem('joinFiles', JSON.stringify(fileData));
              navigate('/joinUser');
            } catch (error) {
              if (error.name === 'QuotaExceededError') {
                alert('파일 크기가 너무 큽니다. 파일을 압축하거나 크기를 줄여주세요.');
              } else {
                alert('데이터 저장 실패: ' + error.message);
              }
            }
          });
          
        } catch (error) {
          alert('데이터 저장 실패: ' + error.message);
          console.error('localStorage 저장 실패:', error);
        }
      } else {
        // PC/웹에서는 기존 방식 (location.state 사용)
        const fileObjects = {
          identification_img: files.adultFile,
          student_id_img: files.studentFile,
          business_registration_img: files.producerFile
        };

        navigate('/joinUser', { 
          state: {
            formData: joinFormData,
            files: fileObjects,
            memberType: memberType
          }
        });
      }
    }
  };

  // 등급별 회원혜택보기 클릭시 새창처리
  const handleViewBenefit = () => {
    window.open('/about/information', '_blank');
  }

  return(
    <div>
      <section className="auth_section ">
          <div className="inner gap_32">
              <div className="section_title">
                  <h3>
                    {joinVerificationInfo.memberKind === "personal" ? t('general_member_registration') : t("business_registration")}
                  </h3>
              </div>
              <div className="form_wrap join_con">
                <h6 className="sub_title">{t('basic_info')}</h6>
                <ul className="form_group join_form">
                  {/* 회원유형 */}
                  <li className="member_type">
                    <span className="label">
                      {t('member_type')}
                      </span>
                    <div className="default_radio radio_list">
                      <div>
                        <input 
                          type="radio" 
                          id="type_general" 
                          value={MEMBER_TYPES.GENERAL}
                          name="memberType" 
                          checked={memberType === MEMBER_TYPES.GENERAL}
                          onChange={handleMemberTypeChange}
                        />
                        <label htmlFor="type_general">{joinVerificationInfo.memberKind === "personal" ? t('general_grade') : t("business_grade")}</label>
                        
                      </div>

                      {joinVerificationInfo.memberKind === "personal" && (
                      <div>
                        <input 
                          type="radio" 
                          id="type_student" 
                          value={MEMBER_TYPES.STUDENT}
                          name="memberType" 
                          checked={memberType === MEMBER_TYPES.STUDENT}
                          onChange={handleMemberTypeChange}
                        />
                        <label htmlFor="type_student">{t('general_student')}</label>
                      </div>
                      )}

                      <div>
                        <input 
                        type="radio" 
                        id="type_director" 
                        value={MEMBER_TYPES.DIRECTOR}
                        name="memberType" 
                        checked={memberType === MEMBER_TYPES.DIRECTOR}
                        onChange={handleMemberTypeChange}
                        />
                        <label htmlFor="type_director">{joinVerificationInfo.memberKind === "personal" ? t('general_cinematographer') : t("business_cinematographer")}</label>
                      </div>

                      {joinVerificationInfo.memberKind === "corporate" && (
                      <div>
                        <input 
                          type="radio"
                          id="type_producer" 
                          value={MEMBER_TYPES.PRODUCER}
                          name="memberType" 
                          checked={memberType === MEMBER_TYPES.PRODUCER}
                          onChange={handleMemberTypeChange}
                          />
                        {/* <label htmlFor="type_producer">{joinVerificationInfo.memberKind === "personal" ? t('general_production_company') : t("business_production_company")}</label> */}
                        <label htmlFor="type_producer">{t('business_production_company')}</label>
                      </div>
                      )}
                      <div className="view_benefit" onClick={handleViewBenefit}>
                        {t('view_benefit')}
                      </div>
                    </div>
                  </li>

                  {/* 이름 */}
                  <li className="member_name default_input lg">
                    <label htmlFor="name">{t('name')}</label>
                    <input 
                      type="text" 
                      id="name"
                      className="readonly" 
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('name_input_txt')}
                      readOnly
                    />
                  </li>

                  {/* 휴대폰 번호 */}
                  <li className="member_phone default_input lg">
                    <label htmlFor="phone">{t('phone_number')}</label>
                    <input 
                      type="tel" 
                      id="phone"
                      className="readonly" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('delivery_address_name3')} 
                      readOnly
                    />
                  </li>


                    {/* 부재 시 연락처 */}
                    <li className="member_email default_input lg">
                      <label htmlFor="member_phone2">{t('alternate_contact')} *</label>
                      <input 
                        type="tel" 
                        id="member_phone2" 
                        value={formData.member_phone2 || ""}
                        onChange={handleInputChange}
                        placeholder={t('alternate_contact_input')} 
                        maxLength="13"
                      />
                  </li>

                  {/* 성별 */}
                  <li className="member_gender">
                    <span className="label">{t('gender')}</span>
                    <div className="default_radio radio_list">
                      <div>
                        <input 
                          type="radio" 
                          id="gender_female" 
                          name="gender" 
                          value="female"
                          checked={personalInfo.gender === 'female'}
                          onChange={handlePersonalInfoChange}
                        />
                        <label htmlFor="gender_female">{t('female')}</label>
                      </div>
                      <div>
                        <input 
                          type="radio" 
                          id="gender_male" 
                          name="gender" 
                          value="male"
                          checked={personalInfo.gender === 'male'}
                          onChange={handlePersonalInfoChange}
                        />
                        <label htmlFor="gender_male">{t('male')}</label>
                      </div>
                    </div>
                  </li>

                  {/* 생년월일 */}
                  <li className="member_birth default_input lg">
                    <label htmlFor="birthDate">{t('birthdate')}</label>
                    <input 
                      type="text" 
                      id="birthDate"
                      className="readonly"
                      value={personalInfo.birthDate}
                      onChange={handlePersonalInfoChange}
                      placeholder="생년월일 (예: 20210307)" 
                      readOnly
                    />
                  </li>

                  {/* 주민등록번호 */}
                  <li className="member_resident default_input lg">
                    <label htmlFor="residentNumber1">{t('resident_number')}</label>
                    <div className="resident_number_group">
                      <input 
                        type="text" 
                        id="residentNumber1"
                        className="readonly"
                        value={personalInfo.residentNumber1}
                        onChange={handlePersonalInfoChange}
                        placeholder="주민등록번호 입력"
                        maxLength="6"
                        readOnly
                      />
                      <input 
                        type="text" 
                        id="residentNumber2"
                        className="readonly"
                        value={personalInfo.residentNumber2}
                        onChange={handlePersonalInfoChange}
                        placeholder=""
                        maxLength="1"
                        readOnly
                      />
                      <span className="masked_digits">******</span>
                    </div>
                  </li>


                  {joinVerificationInfo.memberKind === "corporate" && (
                    <>
                    {/* 신분증 */}
                    <li className="student_id_upload default_input lg">
                      <label htmlFor="adultFile">{t('id_card')}</label>
                      <div className="default_input btn_group lg">
                        <input    
                          type="file" 
                          id="adultFile" 
                          accept="image/*, .pdf" 
                          onChange={(e) => handleFileChange(e, 'adultFile')}
                          style={{ display: 'none' }}
                        />
                        <span className="placeholder form_input">
                          <span className="ellipsis_clamp">
                            {files.adultFile ? files.adultFile.name : t('id_card_upload')}
                          </span>
                        </span>
                        <label className="default_btn light_gray" htmlFor="adultFile">
                          {t('withdrawal_completed_message_16')}
                        </label>
                      </div>
                    </li>
                  </>
                  )}

                  {joinVerificationInfo.memberKind === "corporate" && (
                    <>
                    {/* 회사명 */}
                    <li className="member_email default_input lg">
                      <label htmlFor="business_name">{t('company_name')}</label>
                      <input 
                        type="text" 
                        id="business_name"
                        value={formData.business_name}
                        onChange={handleInputChange}
                        placeholder={t('company_name_input')}
                      />
                    </li>
                  </>
                  )}
                  {joinVerificationInfo.memberKind === "corporate" && (
                    <>
                      {/* 사업자 등록 번호 */}
                      <li className="member_email default_input lg">
                        <label htmlFor="business_num">{t('business_registration_number_1')}</label>
                        <input 
                          type="text" 
                          id="business_num"
                          value={formData.business_num}
                          onChange={handleInputChange}
                          placeholder={t('business_registration_number_4')}
                          className={businessNumError ? 'error' : ''}
                        />
                        {businessNumError && (
                          <p className="error_message" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                            {businessNumError}
                          </p>
                        )}
                      </li>
                    </>
                  )}
                  {joinVerificationInfo.memberKind === "corporate" && (
                    <>
                    {/* 대표자명 */}
                    <li className="member_email default_input lg">
                      <label htmlFor="business_ceo">{t('ceo_name')}</label>
                    <input 
                      type="text" 
                      id="business_ceo"
                      value={formData.business_ceo}
                      onChange={handleInputChange}
                      placeholder={t('business_ceo')}
                    />
                  </li>
                  </>
                  )}
                  <div style={{display: "flex", gap: "4px"}}>
                    {joinVerificationInfo.memberKind === "corporate" && (
                      <>
                      {/* 업태 */}
                      <li className="member_email default_input lg">
                        <label htmlFor="business_category">{t('industry_1')}</label>
                      <input 
                        type="text" 
                        id="business_category"
                        value={formData.business_category}
                        onChange={handleInputChange}
                        placeholder={t('business_category')}
                      />
                    </li>
                    </>
                    )}
                    {joinVerificationInfo.memberKind === "corporate" && (
                      <>
                      {/* 종목 */}
                      <li className="member_email default_input lg">
                        <label htmlFor="business_item">{t('business_registration_number_2')}</label>
                      <input 
                        type="text" 
                        id="business_item"
                        value={formData.business_item}
                        onChange={handleInputChange}
                        placeholder={t('business_item')}
                        />
                      </li>
                    </>
                    )}
                  </div>

                  {/* 이메일 */}
                  <li className="member_email default_input lg">
                    <label htmlFor="email">{joinVerificationInfo.memberKind === "personal" ? t('email') : t('email') + ' (' + t('tax_invoice_receipt') + ')'} *</label>
                    <input 
                      type="text" 
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('email_input')} 
                    />
                  </li>

                  {/* 주소 */}
                  <li className="member_addr default_input lg">
                    <span className="label">{joinVerificationInfo.memberKind === "personal" ? t('address') : t('business_address')} *</span>
                    <div className="flex-8 column search_addr">
                      <div className="btn_group">
                        <input 
                          type="text" 
                          value={personalAddress.zonecode}
                          readOnly 
                        />
                        <button className="default_btn light_gray" type="button" onClick={handlePersonalPostcodeOpen}>
                          {t('postal_code_search')}
                        </button>
                        {isPersonalPostcodeOpen && (
                          <>
                            <div 
                              className="dimmed" 
                              onClick={handlePersonalPostcodeClose}
                              style={{
                                position: 'fixed',
                                width: '100vw',
                                height: '100vh',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: 'rgba(0, 0, 0, 0.50)',
                                zIndex: 101
                              }}
                            />
                            <div className="default_popup">
                              <div className="modal_content">
                                <div className="modal_wrap">
                                  <div className="modal_header">
                                    <button 
                                      type="button" 
                                      className="close_btn" 
                                      onClick={handlePersonalPostcodeClose}
                                    ></button>
                                  </div>
                                  <div className="modal_body">
                                    <DaumPostcodeEmbed
                                      onComplete={handlePersonalPostcodeComplete}
                                      onClose={handlePersonalPostcodeClose}
                                      style={{ height: '460px' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <input 
                        className="" 
                        value={personalAddress.roadAddress}
                        readOnly 
                        type="text" 
                      />
                      <input 
                        className="" 
                        placeholder={t('detailed_address_input')} 
                        type="text" 
                        value={personalDetailAddress}
                        onChange={handlePersonalDetailAddressChange}
                      />
                    </div>
                  </li>
                  {joinVerificationInfo.memberKind === "personal" && (
                    <>
                    {/* 신분증 */}
                    <li className="student_id_upload default_input lg">
                      <label htmlFor="adultFile">{t('id_card')} *</label>
                      <div className="default_input btn_group lg">
                        <input    
                          type="file" 
                          id="adultFile" 
                          accept="image/*, .pdf" 
                          onChange={(e) => handleFileChange(e, 'adultFile')}
                          style={{ display: 'none' }}
                        />
                        <span className="placeholder form_input">
                          <span className="ellipsis_clamp">
                            {files.adultFile ? files.adultFile.name : t('id_card_upload')}
                          </span>
                        </span>
                        <label className="default_btn light_gray" htmlFor="adultFile">
                          {t('withdrawal_completed_message_16')}
                        </label>
                      </div>
                    </li>
                  </>
                  )}
                  {/* 학생증 */}
                  {memberType === MEMBER_TYPES.STUDENT && (
                  <li className="student_id_upload default_input lg">
                    <label htmlFor="studentFile">{t('student_id')}</label>
                    <div className="default_input btn_group lg">
                      <input    
                        type="file" 
                        id="studentFile" 
                        accept="image/*, .pdf" 
                          onChange={(e) => handleFileChange(e, 'studentFile')}
                          style={{ display: 'none' }}
                      />
                      <span className="placeholder form_input">
                          <span className="ellipsis_clamp">
                            {files.studentFile ? files.studentFile.name : t('student_id_upload')}
                          </span>
                      </span>
                        <label className="default_btn light_gray" htmlFor="studentFile">
                        {t('withdrawal_completed_message_16')}
                      </label>
                    </div>
                  </li>
                  )}

                  {/* 사업자등록증 */}
                  {/* {memberType === MEMBER_TYPES.PRODUCER && (
                  <li className="business_registration_upload default_input lg">
                    <label htmlFor="producerFile">{t('withdrawal_completed_message_23')}</label>
                    <div className="default_input btn_group lg">
                      <input 
                        type="file"
                        id="producerFile"
                        accept="image/*, .pdf"
                          onChange={(e) => handleFileChange(e, 'producerFile')}
                        style={{ display: 'none' }}
                      />
                      <span className="placeholder form_input"> 
                          <span className="ellipsis_clamp">
                            {files.producerFile ? files.producerFile.name : t('business_registration_number_upload')}
                          </span>
                      </span>
                      <label 
                        className="default_btn light_gray" 
                        htmlFor="producerFile"
                      >
                        {t('withdrawal_completed_message_16')}
                      </label>
                    </div>
                  </li>
                  )} */}
                  {/* 사업자등록증 */}
                  {(joinVerificationInfo.memberKind === "personal" ? memberType === MEMBER_TYPES.PRODUCER : true) && (
                  <li className="business_registration_upload default_input lg">
                    <label htmlFor="producerFile">{t('withdrawal_completed_message_23')}</label>
                    <div className="default_input btn_group lg">
                      <input 
                        type="file"
                        id="producerFile"
                        accept="image/*, .pdf"
                          onChange={(e) => handleFileChange(e, 'producerFile')}
                        style={{ display: 'none' }}
                      />
                      <span className="placeholder form_input"> 
                          <span className="ellipsis_clamp">
                            {files.producerFile ? files.producerFile.name : t('business_registration_number_upload')}
                          </span>
                      </span>
                      <label 
                        className="default_btn light_gray" 
                        htmlFor="producerFile"
                      >
                        {t('withdrawal_completed_message_16')}
                      </label>
                    </div>
                  </li>
                  )}
                </ul>
              </div>

              {joinVerificationInfo.memberKind === "personal" && (
                <>
                  {/* 사업자 정보 */}
                  {memberType === MEMBER_TYPES.PRODUCER && (
                    <div className="form_wrap join_con" style={{marginTop:"16px"}}>
                      <h6 className="sub_title">{t('business_info')}</h6>
                      <ul className="form_group join_form">
                        <li className="member_phone default_input lg">
                          <label htmlFor="company_name">{t('business_name')}</label>
                          <input 
                            type="text" 
                            id="name"
                            value={formData.companyInfo.name}
                            onChange={handleCompanyInfoChange}
                            placeholder={t('business_name_input')}
                          />
                        </li>
                        <li className="member_email default_input lg">
                          <label htmlFor="businessNumber">{t('business_registration_number')}</label>
                          <input 
                            type="text" 
                            id="businessNumber"
                            value={formData.companyInfo.businessNumber}
                            onChange={handleCompanyInfoChange}
                            placeholder={t('business_registration_number_input')} 
                          />
                        </li>
                        <li className="business_ceo default_input lg">
                          <label htmlFor="business_ceo">{t('ceo')}</label>
                          <input 
                            type="text" 
                            id="business_ceo"
                            value={formData.companyInfo.business_ceo}
                            onChange={handleCompanyInfoChange}
                            placeholder={t('ceo_input')} 
                          />
                        </li>
                        <li className="business_sectors default_input lg">
                          <label htmlFor="businessType">{t('industry')}</label>
                          <input 
                            type="text" 
                            id="businessType"
                            value={formData.companyInfo.businessType}
                            onChange={handleCompanyInfoChange}
                            placeholder={t('industry_input')} 
                          />
                        </li>
                        {/* 주소 */}
                        <li className="business_addr default_input lg">
                          <span className="label">{t('address')}</span>
                          <div className="flex-8 column search_addr">
                            <div className="btn_group">
                              <input 
                                type="text" 
                                value={businessAddress.zonecode}
                                readOnly 
                              />
                              <button className="default_btn light_gray" type="button" onClick={handleBusinessPostcodeOpen}>
                                {t('postal_code_search')}
                              </button>
                              {isBusinessPostcodeOpen && (
                                <>
                                  <div 
                                    className="dimmed" 
                                    onClick={handleBusinessPostcodeClose}
                                    style={{
                                      position: 'fixed',
                                      width: '100vw',
                                      height: '100vh',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      background: 'rgba(0, 0, 0, 0.50)',
                                      zIndex: 101
                                    }}
                                  />
                                  <div className="default_popup">
                                    <div className="modal_content">
                                      <div className="modal_wrap">
                                        <div className="modal_header">
                                          <button
                                            type="button" 
                                            className="close_btn" 
                                            onClick={handleBusinessPostcodeClose}
                                          ></button>
                                        </div>
                                        <div className="modal_body">
                                          <DaumPostcodeEmbed
                                            onComplete={handleBusinessPostcodeComplete}
                                            onClose={handleBusinessPostcodeClose}
                                            style={{ height: '460px' }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            <input 
                              className="" 
                              value={businessAddress.roadAddress}
                              readOnly 
                              type="text" 
                            />
                            <input 
                              className="" 
                              placeholder={t('detailed_address_input')} 
                              type="text"
                              value={formData.companyInfo.address.address2}
                              onChange={handleBusinessDetailAddressChange}
                            />
                          </div>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}

              <button 
                type="button" 
                className="default_btn black lg" 
                disabled={!isFormValid()}
                onClick={handleNextClick}
              >
                {t('next')}
              </button>
          </div>
      </section>
    </div>
  )
};

export default JoinForm;